import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { GridLoader } from "react-spinners";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  buscarSumarizacaoIcmsApuracao,
  buscarLancamentosIcmsE111Apuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import BreadcrumbApuracao from "../components/BreadcrumbApuracao";
import SelectFiltro from "../components/SelectFiltro";
import { formatarCnpj, fmtMesAno } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

function exportarCsv(linhas, nomeArquivo) {
  const cabecalho = [
    "competencia",
    "cod_aj_apur",
    "descr_compl_aj",
    "vl_aj_apur",
    "tem_e112",
    "documento_fiscal_vinculado",
  ].join(";");
  const corpo = linhas.map((l) =>
    [
      l.competencia,
      l.codAjApur,
      l.descrComplAj ?? "",
      l.vlAjApur,
      l.temE112 ? "sim" : "nao",
      l.documentoFiscalVinculado ?? "",
    ]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(";")
  );
  const blob = new Blob([[cabecalho, ...corpo].join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

export default function LancamentosIcmsE111() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const competenciaInicial = searchParams.get("competencia") || "";

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [apuracao, setApuracao] = useState(null);
  const [competencia, setCompetencia] = useState(competenciaInicial);
  const [competenciasDisponiveis, setCompetenciasDisponiveis] = useState([]);
  const [linhas, setLinhas] = useState([]);
  const [linhasPagina, setLinhasPagina] = useState([]);

  useEffect(() => {
    let ativo = true;
    async function carregarApuracaoECompetencias() {
      const res = await buscarSumarizacaoIcmsApuracao(id);
      if (!ativo || !res.sucesso) return;
      setApuracao(res.apuracao || null);
      const ymSet = new Set(
        (res.linhas || []).map((l) => String(l.competencia || "").slice(0, 7))
      );
      setCompetenciasDisponiveis(Array.from(ymSet).sort());
    }
    carregarApuracaoECompetencias();
    return () => {
      ativo = false;
    };
  }, [id]);

  const carregarLancamentos = useCallback(async () => {
    if (!competencia) {
      setLoading(false);
      setErro("Selecione uma competência.");
      return;
    }
    setLoading(true);
    setErro("");
    const res = await buscarLancamentosIcmsE111Apuracao(id, competencia);
    setLoading(false);
    if (!res.sucesso) {
      setErro(res.mensagem || "Não foi possível carregar os lançamentos.");
      return;
    }
    setLinhas(res.linhas);
  }, [id, competencia]);

  useEffect(() => {
    carregarLancamentos();
  }, [carregarLancamentos]);

  function handleCompetenciaChange(novaCompetencia) {
    setCompetencia(novaCompetencia);
    setSearchParams({ competencia: novaCompetencia });
  }

  const competenciaOpcoes = useMemo(() => {
    const base = competenciasDisponiveis.map((ym) => ({ value: ym, label: fmtMesAno(ym) }));
    if (competencia && !competenciasDisponiveis.includes(competencia)) {
      return [{ value: competencia, label: fmtMesAno(competencia) }, ...base];
    }
    return base;
  }, [competenciasDisponiveis, competencia]);

  return (
    <BreadcrumbApuracao
      itens={[
        { label: "Apurações", onClick: () => navigate("/relatorios/apuracoes") },
        {
          label: `Apuração ICMS — ${apuracao?.empresa || "—"}`,
          onClick: () => navigate(`/relatorios/apuracoes/${id}/detalhe/icms?tab=icms`),
        },
        { label: "Lançamentos ICMS — E111" },
      ]}
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        A coluna "Tipo" (débito/crédito/estorno) não é exibida — não há, no sistema, uma
        tabela de domínio confiável para mapear o prefixo do código do ajuste
        (COD_AJ_APUR). "E112 vinculado" indica se o ajuste referencia um documento
        fiscal específico; quando presente, o documento exibido ainda não foi validado
        contra dado real de produção.
      </Alert>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <ApuracaoCardShell
        hideContainer
        innerCardSx={{ border: "none", margin: 0 }}
        conteudoComScrollProprio
        Icone={AssessmentIcon}
        titulo="Lançamentos ICMS — ajustes E111"
        subtitulo={
          <>
            {apuracao?.empresa || "—"} · {formatarCnpj(apuracao?.cnpj)}
            <br />
            Competência: {competencia ? fmtMesAno(competencia) : "—"} · registro E111
            (Ajuste/Benefício/Incentivo da Apuração do ICMS)
          </>
        }
        acaoHeader={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                {linhas.length} ajustes no filtro
              </Typography>
              <Button
                variant="contained"
                size="small"
                disabled={linhas.length === 0}
                onClick={() =>
                  exportarCsv(linhas, `lancamentos_icms_e111_${formatarCnpj(apuracao?.cnpj)}_${competencia}.csv`)
                }
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
              >
                Exportar CSV
              </Button>
            </Box>
          }
          filtros={
            <SelectFiltro
              label="Competência"
              value={competencia}
              onChange={handleCompetenciaChange}
              options={competenciaOpcoes}
            />
          }
          paginacao={
            !loading &&
            linhas.length > 0 && (
              <Pagination
                paginationKey="lancamentos-icms-e111"
                data={linhas}
                filteredData={linhasPagina}
                setFilteredData={setLinhasPagina}
              />
            )
          }
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <GridLoader color="#1565c0" size={10} />
            </Box>
          ) : linhas.length === 0 && !erro ? (
            <p className={styles.emptyMsg}>Nenhum ajuste E111 encontrado para esta competência.</p>
          ) : (
              <div style={{ margin: "8px 0 0", overflow: "auto" }}>
                <table className={tableStyles.accordion_table}>
                  <thead>
                    <tr>
                      <th scope="col">CNPJ</th>
                      <th scope="col">Competência</th>
                      <th scope="col">Código do ajuste</th>
                      <th scope="col">Descrição complementar</th>
                      <th scope="col" style={{ textAlign: "right" }}>Valor do ajuste</th>
                      <th scope="col">E112 vinculado</th>
                      <th scope="col">Documento fiscal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhasPagina.map((l, i) => (
                      <tr key={`${l.codAjApur}-${i}`}>
                        <td style={{ fontFamily: "ui-monospace, monospace" }}>{formatarCnpj(apuracao?.cnpj)}</td>
                        {/* backend já retorna "MM/YYYY" (to_char(dt_apuracao_ini, 'MM/YYYY')) */}
                        <td>{l.competencia || "—"}</td>
                        <td style={{ fontFamily: "ui-monospace, monospace" }}>{l.codAjApur || "—"}</td>
                        <td>{l.descrComplAj || "—"}</td>
                        <td style={{ textAlign: "right", fontFamily: "ui-monospace, monospace" }}>{formatarMoedaBRL(l.vlAjApur)}</td>
                        <td>{l.temE112 ? "Sim" : "Não"}</td>
                        <td>{l.documentoFiscalVinculado || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          )}
      </ApuracaoCardShell>
    </BreadcrumbApuracao>
  );
}
